import { useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import AnalyticsIcon from '@mui/icons-material/AnalyticsOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import { useModel } from '../hooks/useModel';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { useConfig } from '../store/config';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { driftInRelease, type Epic, type EpicDrift } from '../lib/model';
import { getKnownReleases, detectRelease, releaseOfIssue } from '../lib/release-detect';
import { sortReleasesByStart, colorForLane } from '../lib/releases';
import { fmtInt } from '../lib/format';
import { StatusChip } from './StatusChip';

type SortKey = 'drift' | 'confidence' | 'refined' | 'rough' | 'key';
type SortDir = 'asc' | 'desc';

// Tabelle, die für jedes Epic den Drift zwischen Grobplanung (Rough) und
// Feinplanung (Σ SP/Estimate der refined Kids) zeigt — plus einen
// Confidence-Score, der signalisiert, wie sehr man der Detailschätzung
// schon trauen kann.
export function EpicDriftTable() {
  const model = useModel();
  const visibleRows = useFilteredRows();
  const releaseCal = useConfig(s => s.releaseCal);
  const rows = useData(s => s.rows);
  // Team-Selector ist bidirektional an den globalen Filter gebunden.
  const effectiveTeam = useFilters(s => s.team);
  const setFilters    = useFilters(s => s.set);

  const [release, setRelease] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('drift');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [onlyWithRough, setOnlyWithRough] = useState(true);

  // Globalen Team-Filter als Default; lokale Auswahl überschreibt ihn

  // Teams aus den Daten, sortiert nach Häufigkeit
  const teams = useMemo(() => sortedByCount(countBy(rows, 'Team Name')).map(([n]) => n), [rows]);

  // Filter-aware: ein Epic ist sichtbar, wenn das Epic oder eines seiner
  // Kids durch den Filter geht (gleiche Logik wie EpicsTable).
  const visibleKeys = useMemo(() => new Set(visibleRows.map(r => r['Issue key'])), [visibleRows]);
  const known = useMemo(() => getKnownReleases(Object.keys(releaseCal)), [releaseCal]);

  const filtered = useMemo<{ epic: Epic; drift: EpicDrift; release: string }[]>(() => {
    return model.epics
      .filter(e => !e._isOrphanContainer)
      .map(e => ({
        epic: e,
        // Drift nur über Child items, die zum Release des Epics passen.
        // Kids mit eigener Fix Version eines anderen Releases werden
        // separat als 'Child items aus anderem Release' ausgewiesen.
        drift: driftInRelease(e, model.byKey, known, detectRelease, releaseOfIssue),
        release: detectRelease(e, known),
      }))
      .filter(({ epic }) => {
        // Filter-aware (globale Filter aus TeamBar/FilterBar)
        if (visibleKeys.has(epic['Issue key'])) return true;
        return (epic._kids ?? []).some(k => visibleKeys.has(k['Issue key']));
      })
      .filter(({ epic }) => !effectiveTeam || epic['Team Name'] === effectiveTeam)
      .filter(({ release: r }) => !release || r === release)
      .filter(({ epic, drift }) => {
        if (onlyWithRough && drift.rough <= 0 && (epic._kidCount ?? 0) === 0) return false;
        return true;
      });
  }, [model.epics, visibleKeys, known, release, effectiveTeam, onlyWithRough]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let va: number, vb: number;
      switch (sortKey) {
        case 'drift':      va = Math.abs(a.drift.drift);                  vb = Math.abs(b.drift.drift);                  break;
        case 'confidence': va = a.drift.confidencePct ?? -1;              vb = b.drift.confidencePct ?? -1;              break;
        case 'refined':    va = a.epic._refinedCount;                     vb = b.epic._refinedCount;                     break;
        case 'rough':      va = a.drift.rough;                            vb = b.drift.rough;                            break;
        case 'key':        return sortDir === 'asc'
          ? (a.epic['Issue key'] ?? '').localeCompare(b.epic['Issue key'] ?? '')
          : (b.epic['Issue key'] ?? '').localeCompare(a.epic['Issue key'] ?? '');
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  // Aggregat-Stats über die aktuell sichtbaren Epics
  const agg = useMemo(() => {
    let sumRough = 0, sumRefined = 0, signedDrift = 0, absDrift = 0;
    let nEpicsWithKids = 0, sumConfidence = 0, nConfidence = 0;
    let sumKidsOutOfRelease = 0;
    for (const { epic, drift } of filtered) {
      sumRough    += drift.rough;
      sumRefined  += drift.refinedPlan;
      signedDrift += drift.drift;
      absDrift    += Math.abs(drift.drift);
      if (epic._kidCount > 0) nEpicsWithKids++;
      if (drift.confidencePct != null) { sumConfidence += drift.confidencePct; nConfidence++; }
      sumKidsOutOfRelease += drift.kidsOutOfRelease;
    }
    const avgConfidence = nConfidence > 0 ? sumConfidence / nConfidence : null;
    const sumDriftPct   = sumRough > 0 ? (signedDrift / sumRough) * 100 : null;
    return { sumRough, sumRefined, signedDrift, absDrift, sumDriftPct, avgConfidence, nEpicsWithKids, sumKidsOutOfRelease };
  }, [filtered]);

  const releases = useMemo(() => sortReleasesByStart(releaseCal), [releaseCal]);
  const visibleReleases = ['', ...releases];

  if (!model.epics.filter(e => !e._isOrphanContainer).length) return null;

  function setSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  }

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          <AnalyticsIcon color="primary" />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="overline" color="text.secondary">Epic-Drift &amp; Confidence</Typography>
            <Typography variant="body2" color="text.secondary">
              Pro Epic: wie weit liegt die Detailplanung (Σ refined Child items) von der Grobplanung (Rough) ab? Wie verlässlich ist der Wert?
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Team</InputLabel>
            <Select
              value={effectiveTeam}
              label="Team"
              onChange={(e) => setFilters({ team: e.target.value })}
              startAdornment={<GroupsIcon fontSize="small" sx={{ mr: 0.5, color: 'action.active' }} />}
            >
              <MenuItem value=""><em>Alle Teams</em></MenuItem>
              {teams.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Release</InputLabel>
            <Select value={release} label="Release" onChange={(e) => setRelease(e.target.value)}>
              {visibleReleases.map(r => (
                <MenuItem key={r || '__all'} value={r}>{r || 'Alle Releases'}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip
            label={onlyWithRough ? 'nur mit Rough/Child items' : 'alle Epics'}
            size="small"
            onClick={() => setOnlyWithRough(v => !v)}
            color={onlyWithRough ? 'primary' : 'default'}
            variant={onlyWithRough ? 'filled' : 'outlined'}
            clickable
          />
        </Stack>

        {/* Aggregate */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <SummaryStat label="Epics"                value={`${fmtInt(filtered.length)}`} sub={`davon ${agg.nEpicsWithKids} mit Child items`} />
          <SummaryStat label="Σ Rough"              value={`${fmtInt(agg.sumRough)} PT`}     />
          <SummaryStat label="Σ Refined Child items" value={`${fmtInt(agg.sumRefined)} PT`}   />
          <SummaryStat
            label="Drift (refined − rough)"
            value={`${agg.signedDrift >= 0 ? '+' : ''}${fmtInt(agg.signedDrift)} PT`}
            sub={agg.sumDriftPct == null ? '–' : `${agg.sumDriftPct > 0 ? '+' : ''}${Math.round(agg.sumDriftPct)} %`}
            color={agg.signedDrift > 0 ? '#1976d2' : agg.signedDrift < 0 ? '#c62828' : 'inherit'}
          />
          <SummaryStat
            label="Ø Confidence"
            value={agg.avgConfidence == null ? '–' : `${Math.round(agg.avgConfidence)} %`}
            color={confidenceColor(agg.avgConfidence)}
          />
        </Stack>

        <TableContainer sx={{ borderRadius: 2, border: 1, borderColor: 'divider', maxHeight: '70vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 130, cursor: 'pointer', userSelect: 'none' }} onClick={() => setSort('key')}>
                  Key {sortKey === 'key' && (sortDir === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell>Summary</TableCell>
                <TableCell sx={{ width: 110 }}>Release</TableCell>
                <TableCell sx={{ width: 130 }}>Status</TableCell>
                <TableCell sx={{ width: 100 }}>Team</TableCell>
                <TableCell align="right" sx={{ width: 70, cursor: 'pointer', userSelect: 'none' }} onClick={() => setSort('rough')}>
                  Rough {sortKey === 'rough' && (sortDir === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Σ refined Child items</TableCell>
                <TableCell align="right" sx={{ width: 110, cursor: 'pointer', userSelect: 'none' }} onClick={() => setSort('drift')}>
                  Drift {sortKey === 'drift' && (sortDir === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell align="center" sx={{ width: 110, cursor: 'pointer', userSelect: 'none' }} onClick={() => setSort('refined')}>
                  Refinement {sortKey === 'refined' && (sortDir === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell align="center" sx={{ width: 140, cursor: 'pointer', userSelect: 'none' }} onClick={() => setSort('confidence')}>
                  Confidence {sortKey === 'confidence' && (sortDir === 'asc' ? '↑' : '↓')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map(({ epic, drift, release: rel }) => (
                <DriftRow key={epic['Issue key']} epic={epic} drift={drift} release={rel} known={known} releaseCal={releaseCal} />
              ))}
              {!sorted.length && (
                <TableRow><TableCell colSpan={10} sx={{ color: 'text.secondary', fontStyle: 'italic', py: 4, textAlign: 'center' }}>
                  Keine Epics in dieser Auswahl. Filter weiter öffnen oder anderen Release wählen.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          <b>Drift</b> = Σ-refined-Child-items − Rough · zählt <b>nur Child items, deren Fix Version
          zum gleichen Release wie das Epic gehört</b>. Items, die nachträglich in ein anderes Release
          verschoben wurden, fließen NICHT mit ein
          {agg.sumKidsOutOfRelease > 0 && (
            <> — aktuell <b style={{ color: '#ef6c00' }}>{fmtInt(agg.sumKidsOutOfRelease)} Child items</b> in den
            gefilterten Epics gehören zu einem anderen Release oder haben keine Release-Zuordnung.</>
          )}
          <br />
          <b>Confidence</b> = Mittelwert aus „% refined" und „% mit SP gepflegt" über die release-konsistenten Child items.
        </Typography>
      </CardContent>
    </Card>
  );
}

function DriftRow({ epic, drift, release, releaseCal, known }: {
  epic: Epic;
  drift: EpicDrift;
  release: string;
  known: string[];
  releaseCal: Record<string, unknown>;
}) {
  const releaseColor = (() => {
    const idx = Object.keys(releaseCal).indexOf(release);
    return idx >= 0 ? colorForLane(idx) : '#9e9e9e';
  })();
  void known;

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {epic['Issue key']}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" noWrap title={epic['Summary']}>{epic['Summary']}</Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={release}
          size="small"
          sx={{ bgcolor: `${releaseColor}1f`, color: releaseColor, fontWeight: 600 }}
        />
      </TableCell>
      <TableCell><StatusChip status={epic['Status']} /></TableCell>
      <TableCell>
        {epic['Team Name'] ? <Chip label={epic['Team Name']} size="small" variant="outlined" /> : null}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {drift.rough > 0 ? fmtInt(drift.rough) : <Typography variant="caption" color="text.disabled">–</Typography>}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {fmtInt(drift.refinedPlan)}
      </TableCell>
      <TableCell align="right">
        <DriftCell drift={drift} />
      </TableCell>
      <TableCell align="center">
        <RefinementProgress epic={epic} />
      </TableCell>
      <TableCell align="center">
        <ConfidenceCell drift={drift} />
      </TableCell>
    </TableRow>
  );
}

// ── Sub-Cells ──────────────────────────────────────────────────────

function DriftCell({ drift }: { drift: EpicDrift }) {
  if (drift.rough === 0 && drift.refinedPlan === 0) {
    return <Typography variant="caption" color="text.disabled">–</Typography>;
  }
  const Icon = drift.drift > 0 ? TrendingUpIcon : drift.drift < 0 ? TrendingDownIcon : TrendingFlatIcon;
  const color = drift.drift > 0 ? '#1976d2' : drift.drift < 0 ? '#c62828' : 'inherit';
  const sign  = drift.drift > 0 ? '+' : '';
  return (
    <Tooltip arrow title={`Σ refined ${fmtInt(drift.refinedPlan)} − Rough ${fmtInt(drift.rough)} = ${sign}${fmtInt(drift.drift)} PT`}>
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ color }}>
        <Icon fontSize="small" />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {sign}{fmtInt(drift.drift)} PT
          </Typography>
          {drift.driftPct != null && (
            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', opacity: 0.8 }}>
              {sign}{Math.round(drift.driftPct)} %
            </Typography>
          )}
        </Box>
      </Stack>
    </Tooltip>
  );
}

function RefinementProgress({ epic }: { epic: Epic }) {
  if (epic._kidCount === 0) {
    return <Typography variant="caption" color="text.disabled">keine Child items</Typography>;
  }
  const refinedPct = (epic._refinedCount / epic._kidCount) * 100;
  return (
    <Tooltip arrow title={`${epic._refinedCount} von ${epic._kidCount} Child items refined · ${epic._doneCount} done`}>
      <Box>
        <LinearProgress
          variant="determinate"
          value={refinedPct}
          sx={{ height: 6, borderRadius: 3 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {epic._refinedCount} / {epic._kidCount}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function ConfidenceCell({ drift }: { drift: EpicDrift }) {
  if (drift.confidencePct == null) {
    return <Typography variant="caption" color="text.disabled">–</Typography>;
  }
  const v = drift.confidencePct;
  const color = confidenceColor(v);
  return (
    <Tooltip arrow title={`Refined: ${Math.round(drift.refinementPct ?? 0)}% · SP gepflegt: ${Math.round(drift.spCoveragePct ?? 0)}%`}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 110 }}>
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={v}
            sx={{
              height: 6, borderRadius: 3,
              backgroundColor: 'action.hover',
              '& .MuiLinearProgress-bar': { backgroundColor: color },
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color, minWidth: 32, textAlign: 'right' }}>
          {Math.round(v)}%
        </Typography>
      </Stack>
    </Tooltip>
  );
}

function SummaryStat({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Card variant="outlined" sx={{ p: 1.5, minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, color: color ?? 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Card>
  );
}

function confidenceColor(v: number | null): string {
  if (v == null) return 'inherit';
  if (v >= 75) return '#2e7d32';   // grün — sehr belastbar
  if (v >= 50) return '#f57c00';   // orange — mittel
  return '#c62828';                // rot — niedrig
}
